import BaseButton from '@/components/Common/DesignSystem/BaseButton/BaseButton';
import IconCommon from '@/components/Common/DesignSystem/Icon/IconCommon';
import {
  RAFFLE_DEAL__ERROR_MAP,
  RAFFLE_DEAL_LOGIN,
  RAFFLE_DEAL_LOGOUT,
  RAFFLE_DEAL_PAGE_NAME,
  RAFFLE_DEAL_PAGE_TYPE,
} from '@/components/RaffleDeal/constant';
import { IRaffleDealItem } from '@/models/RaffleDeal/interfaces/IRaffleDealPromotionsPayload';
import { IRaffleDealV3WinHistoryContent } from '@/models/RaffleDeal/interfaces/IRaffleDealWinHistoryPayload';
import { Kinesis } from '@/services/TrackerService';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { RaffleDealStoreCtx } from '@/stores/RaffleDealStore';
import { numberFormat } from '@/utils';
import useRaffleDealV3ParticipateMutation from '@/utils/hooks/react_query/promotion/raffle-deal/useRaffleDealV3ParticipateMutation';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import React, { useContext, useMemo, useState } from 'react';

import RaffleDealWinnerBottomSheet from '../WinnerBottomSheet/RaffleDealWinnerBottomSheet';
import RaffleDealApplyCompletePopup from './RaffleDealApplyCompletePopup';
import styles from './RaffleDealProductUnit.module.scss';

interface IRaffleDealProductUnitProps {
  deal: IRaffleDealItem | IRaffleDealV3WinHistoryContent;
}

type TRaffleDealButton = {
  label: string;
  onClick?: () => void | Promise<void>;
};

const RaffleDealProductUnit: React.FC<IRaffleDealProductUnitProps> = ({ deal }) => {
  const [isOpenWinnerList, setIsOpenWinnerList] = useState(false);

  const { participantsCount, participantsCountedAt } = deal;
  const store = useContext(RaffleDealStoreCtx);
  const globalStore = useContext(GlobalStoreCtx);

  // 상품 상세 링크
  const productUrlLink = useMemo(() => deal.productUrl, [deal.productUrl]);

  // 당첨 후
  const drawAt = moment(deal.drawAt);
  const isDrawed = store.now.isSameOrAfter(drawAt);

  // 참여 인원, 기간 정보
  const isShowCount = useMemo(() => participantsCount > 0, [participantsCount]);
  const participantsCountAt = useMemo(() => {
    if (store.now.isAfter(deal.endAt)) {
      return `${moment(deal.startAt).format('YY년 M월 D일')} ~ ${moment(deal.endAt).format('M월 D일')}`;
    }
    return moment(participantsCountedAt || '').format('a h시 기준');
  }, [participantsCount]);

  // 명단보기
  const openWinnerList = async ({ promotionId, dealId }: { promotionId: number; dealId: number }): Promise<void> => {
    await store.getWinners({
      promotionId,
      dealId,
    });
    if (store.winners.length === 0) {
      globalStore.toast.addItem({
        key: 'no-winner-list',
        text: `명단 내역이 없습니다.`,
        timeout: 2000,
      });
    } else if (store.winners.length > 0) {
      setIsOpenWinnerList(true);
    }
  };

  const { mutate: mutateParticipate, isLoading: isParticipateLoading } = useRaffleDealV3ParticipateMutation({
    onSuccess: ({ popup }) => {
      const { title, content: description, buttonType } = popup;
      const isTwoButton = buttonType === 'TWO_BUTTON';
      store.openPopup({
        children: {
          title,
          description,
        },
        mainBtn: {
          label: isTwoButton ? '쿠폰함 확인하기' : '확인',
          onClick: () => {
            if (isTwoButton) {
              store.isOpenSuccessPopup = false;
              store.goToCoupon();
            } else {
              store.closePopupAndReload();
            }
          },
        },
        subBtn: isTwoButton
          ? {
              label: '닫기',
              onClick: () => {
                store.closePopupAndReload();
              },
            }
          : undefined,
      });
    },
    onError: ({ data }) => {
      globalStore.toast.addItem({
        key: 'raffledeal-participate-error',
        text: RAFFLE_DEAL__ERROR_MAP[data?.code || 'SERVER_ERROR'],
        timeout: 5000,
      });
    },
  });

  // 버튼
  const button: TRaffleDealButton = useMemo(() => {
    if (!store.isLoggedIn) {
      return {
        label: '로그인하고 응모하기',
        onClick: () => store.goToLogin(`${window.location.pathname}`),
      };
    }

    if (store.now.isBefore(moment(deal.startAt))) {
      return {
        label: '상품을 준비중입니다',
      };
    }

    // 당첨 후
    if (isDrawed) {
      // 당첨된 경우
      if (deal.participatedAt && 'winnerFormUrl' in deal) {
        // 종료일  기준 30일 이후 정보입력 버튼 비활성화
        const after30Days = moment(deal.endAt).clone().add('30', 'days');
        if (store.now.isAfter(after30Days)) {
          return {
            label: '이미 종료되었어요',
          };
        }
        return {
          label: '경품 수령을 위한 정보 입력',
          onClick: () => store.goToWinnerForm(deal.winnerFormUrl),
        };
      }
      return {
        label: '이미 종료되었어요',
      };
    }

    // 당첨 전
    if (deal.participatedAt || store.now.isAfter(deal.endAt)) {
      return {
        label: drawAt.format('M월 D일 a h시 당첨자 발표'),
      };
    }

    // 응모 종료
    if (store.now.isAfter(moment(deal.endAt))) {
      return {
        label: '이미 종료되었어요',
      };
    }

    // 응모 안함
    return {
      label: '응모하기',
      onClick: () => {
        Kinesis.click(RAFFLE_DEAL_PAGE_NAME, {
          object: '응모하기 버튼 클릭',
          pageType: RAFFLE_DEAL_PAGE_TYPE,
          promotionName: `${store.PromotionTitle} 프로모션`,
          promotionDetail: store.groupCode,
          CustomerStatus: store.isLoggedIn ? RAFFLE_DEAL_LOGIN : RAFFLE_DEAL_LOGOUT,
        });

        mutateParticipate({
          body: { promotionId: store.PromotionData.promotion.id, dealId: deal.id },
          query: {
            requestAt: store.now.format('YYYY-MM-DDTHH:mm:ss'),
          },
        });
      },
    };
  }, [deal, store.isLoggedIn, isDrawed]);

  return (
    <>
      <div className={styles.container}>
        <img className={styles.image} src={deal.imageUrl} alt="NOL드로우 상품 이미지" />

        <div className={styles.wrap}>
          <p className={styles.productName}>{deal.productName}</p>
          <p className={styles.productDesc}>{deal.productDescription}</p>
          <div className={styles.winningInfoWrap}>
            <p>당첨인원 총 {deal.winnerCount}명</p>
            {isDrawed && 'promotionId' in deal && (
              <a
                className={styles.winnerListBtn}
                onClick={() => openWinnerList({ promotionId: deal.promotionId, dealId: deal.id })}
              >
                명단보기
                <IconCommon type="icon_arrowforward_fill" className={styles.icon} />
              </a>
            )}
          </div>
          <div className={styles.priceInfoWrap}>
            {isShowCount && (
              <div className={styles.countInfo}>
                {participantsCountAt}
                <br />총 {numberFormat(participantsCount)}명 참여
              </div>
            )}
            <div>
              <p className={styles.originPrice}>{deal.regularPriceText}</p>
              <div className={styles.priceWrap}>
                <p className={styles.percent}>{deal.discountRateText}</p>
                <p className={styles.price}>{deal.discountPriceText}</p>
              </div>
            </div>
          </div>
        </div>

        <BaseButton
          as="button"
          className={clsx([styles.actionBtn, !button.onClick && styles.disabled])}
          onClick={button.onClick}
          disabled={isParticipateLoading}
        >
          {button.label}
        </BaseButton>

        {store.activeTab === 'DRAW' && productUrlLink && (
          <BaseButton
            as="button"
            className={styles.productDetailBtn}
            onClick={() => (window.location.href = productUrlLink)}
          >
            상품 자세히 보기
            <IconCommon className={styles.icon} type="icon_arrowforward_fill" />
          </BaseButton>
        )}
      </div>

      <RaffleDealApplyCompletePopup />
      <RaffleDealWinnerBottomSheet isOpen={isOpenWinnerList} close={() => setIsOpenWinnerList(false)} deal={deal} />
    </>
  );
};

export default observer(RaffleDealProductUnit);
